import { useMemo, useState } from "react";
import { DataGrid, GridColDef, gridClasses } from "@mui/x-data-grid";
import styled from "@emotion/styled";
import { Alert, IconButton, Link, TextField } from "@mui/material";
import useRepository from "../hooks/useRepository";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import "../assets/style.css";
import Footer from "./Footer";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ReactLoading from "react-loading";
import { CHAIN_SHEET_ROWS } from "../config/chainSheets";

const columns: GridColDef[] = [
  {
    field: "noi_nhan",
    renderHeader: () => <strong>{"Nơi nhận"}</strong>,
    headerClassName: "class-header",
    flex: 1,
    sortable: false,
    renderCell: (e) => <strong>{e.row.noi_nhan}</strong>,
  },
  {
    field: "ma_chung_tu_doi_tac",
    headerName: "Mã chứng từ đối tác",
    flex: 1,
    sortable: false,
  },
  {
    field: "ghi_chu_noi_dung",
    headerName: "Ghi chú nội dung",
    flex: 1,
    sortable: false,
  },
  {
    field: "thoi_gian_tao",
    headerName: "Thời gian tạo vận đơn",
    flex: 1,
    sortable: true,
  },
  {
    field: "actions",
    headerName: "",
    flex: 1,
    sortable: false,
    renderCell: (e) => {
      const code = e.row.ma_chung_tu_doi_tac;
      if (!code) {
        return <span>Copy mã vận đơn và kiểm tra hành trình trên App MWG</span>;
      }

      let url: string | null = null;
      if (code.includes("NT")) {
        url = `https://ntlogistics.vn/tra-van-don.html?bill=${code}`;
      } else if (code.startsWith("TGD") || code.includes("TGD")) {
        url = `https://viettelpost.com.vn/?q=${code}`;
      }

      return url ? (
        <Link
          target="_blank"
          rel="noopener noreferrer"
          href={url}
          underline="hover"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            lineHeight: 1,
          }}
        >
          <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />
          Xem chi tiết
        </Link>
      ) : (
        <span>Copy mã vận đơn và kiểm tra hành trình trên App MWG</span>
      );
    },
  },
];

const AppStyledProvider = styled.div`
  width: 95%;
  margin: auto;
`;
const StyledTextField = styled(TextField)`
  margin: 10px 0;
  width: 30%;
`;

function RepositoryTable() {
  const [searchWord, setSearchWord] = useState("");
  const { repositoryList, loading, error } = useRepository();

  const filterRepositoryList = useMemo(() => {
    if (!searchWord) return repositoryList;
    return repositoryList.filter((value) => {
      const id = value.noi_nhan.split(" - ")[0];
      return id === searchWord;
    });
  }, [repositoryList, searchWord]);

  return (
    <div>
      <AppStyledProvider>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <div className="header-logo-link">
          <div className="width-40">
            <h2 className="text-align">Tra cứu vận đơn POSM Marketing</h2>
            <StyledTextField
              value={searchWord}
              style={{ width: "100%" }}
              onChange={(event) => setSearchWord(event.target.value)}
              label="Nhập chính xác mã kho để tìm kiếm"
              variant="outlined"
              disabled={loading || !!error}
              InputProps={{
                endAdornment: searchWord && (
                  <IconButton
                    aria-label="Xóa từ khóa tìm kiếm"
                    onClick={() => setSearchWord("")}
                  >
                    <CancelRoundedIcon />
                  </IconButton>
                ),
              }}
            />
          </div>

          <div className="width-70">
            <h2>Nhấn vào logo để xem phân bổ POSM theo chuỗi</h2>
            {CHAIN_SHEET_ROWS.map((row, rowIndex) => (
              <div key={rowIndex}>
                {row.map((chain) => (
                  <a
                    key={chain.href}
                    href={chain.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="logo-link-a"
                  >
                    <img src={chain.src} alt={chain.alt} />
                  </a>
                ))}
              </div>
            ))}
          </div>
        </div>

        {searchWord && !loading && !error && (
          <DataGrid
            getRowHeight={() => "auto"}
            getRowId={(row) => row.id}
            rows={filterRepositoryList}
            columns={columns}
            pageSize={25}
            disableColumnMenu
            disableColumnFilter
            disableColumnSelector
            sx={{
              [`& .${gridClasses.cell}`]: {
                py: 1,
              },
            }}
            autoHeight
          />
        )}

        {loading && (
          <ReactLoading type="spin" color="black" className="loading" />
        )}
      </AppStyledProvider>
      <Footer />
    </div>
  );
}

export default RepositoryTable;
